import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthFlowCard } from "../auth-flow-card";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { stripePromise } from "@/lib/stripe/stripe-promise";
import { useStripeSetupIntent } from "./hooks/use-stripe-setup-intent";
import { BillingInterval, CreatePlanSubscriptionInput, useCreateStripeCompanySubscription } from "./hooks/use-create-stripe-company-subscription";
import { SubscriptionTier } from "./types";
import { getChargedBillingInterval } from "./get-charged-billing-interval";
import { useSyncStripeCompanySetupIntent } from "./hooks/use-sync-stripe-company-intent";
import { Button } from "@repo/ui/components/ui/button";
import { LoadingSpinner } from "@repo/ui/components/ui/loading-spinner";
import { formatCurrency } from "@repo/ui/utils/format-currency";
import { Body } from "@repo/ui/components/ui/text";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { Info } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@repo/ui/components/ui/form";
import { AUTH_FLOW_INPUT_CLASSNAME } from "../constants";
import { POSTAL_FIELD_BY_COUNTRY, SUPPORTED_COUNTRIES_NOTICE, SUPPORTED_SIGNUP_COUNTRY_OPTIONS, type SupportedSignupCountry } from "./constants";
import { cn } from "@repo/common/utils/cn";
import { useActivePlans } from "@/components/pricing/hooks/use-active-plans";
import { type PromoCodeValidationResult, useValidatePromoCode } from "./hooks/use-validate-promo-code";
const PROMO_CODE_INVALID_MESSAGE = "That code doesn't look right — double check and try again";
const purchaseFormSchema = z.object({
  promoCode: z.string().optional()
});
type PurchaseFormValues = z.input<typeof purchaseFormSchema>;
type PurchaseProps = {
  wrapInCard?: boolean;
  selectedPlan: SubscriptionTier;
  billingInterval: BillingInterval;
  hidePlanChange?: boolean;
  onChangePlan?: () => void;
  onPaymentSuccess?: () => void;
};
type PurchaseFormProps = PurchaseProps & {
  setupIntentId: string;
};
export function PurchaseForm({
  setupIntentId,
  selectedPlan,
  billingInterval,
  hidePlanChange,
  onChangePlan,
  onPaymentSuccess
}: PurchaseFormProps) {
  // The PaymentElement reports card completeness; the billing country + postal
  // code are collected by our own inputs (below) so the country list can be
  // restricted to supported countries.
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [billingAddress, setBillingAddress] = useState<{
    postalCode?: string | null;
    country?: string | null;
  } | null>(null);
  const [promoValidation, setPromoValidation] = useState<PromoCodeValidationResult | null>(null);
  const [validatedPromoCode, setValidatedPromoCode] = useState<string | null>(null);
  const setupIntentConfirmedRef = useRef(false);
  const stripe = useStripe();
  const elements = useElements();
  const form = useForm<PurchaseFormValues>({
    resolver: zodResolver(purchaseFormSchema),
    defaultValues: {
      promoCode: ""
    }
  });
  useEffect(() => {
    setupIntentConfirmedRef.current = false;
  }, [setupIntentId]);
  const plans = useActivePlans();
  const planDetails = plans?.find(plan => {
    if (selectedPlan === "SOLO") {
      return plan.tier.id === "SOLO";
    }
    return plan.tier.id === selectedPlan && plan.billingInterval === billingInterval;
  });
  const basePrice = Number(planDetails?.price ?? 0);
  const discountedPrice = promoValidation?.isValid === true && promoValidation.discountedTotalCents != null && promoValidation.originalTotalCents != null && promoValidation.discountedTotalCents < promoValidation.originalTotalCents ? promoValidation.discountedTotalCents / 100 : null;
  const hasDiscount = discountedPrice !== null;
  const isFree = (discountedPrice ?? basePrice) === 0;
  const priceToPay = formatCurrency({
    value: discountedPrice ?? basePrice,
    decimalPlaces: 0
  });
  const basePriceFormatted = formatCurrency({
    value: basePrice,
    decimalPlaces: 0
  });
  const getSubscriptionBillingInterval = (): BillingInterval => getChargedBillingInterval(selectedPlan, billingInterval);
  const {
    mutateAsync: validatePromoCode,
    isPending: isValidatingPromo
  } = useValidatePromoCode();
  const {
    mutateAsync: syncStripeCompanySetupIntent
  } = useSyncStripeCompanySetupIntent();
  const resetPromoValidation = () => {
    setPromoValidation(null);
    setValidatedPromoCode(null);
  };
  const handlePromoCodeBlur = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      form.clearErrors("promoCode");
      resetPromoValidation();
      return;
    }
    try {
      const result = await validatePromoCode({
        tier: selectedPlan as CreatePlanSubscriptionInput["tier"],
        billingInterval: getSubscriptionBillingInterval(),
        promoCode: trimmed
      });
      if (result.isValid) {
        setPromoValidation(result);
        setValidatedPromoCode(trimmed);
        form.clearErrors("promoCode");
        return;
      }
      resetPromoValidation();
      if (result.error?.reason === "UNAVAILABLE") {
        return;
      }
      form.setError("promoCode", {
        message: result.error?.message ?? PROMO_CODE_INVALID_MESSAGE
      });
    } catch {
      resetPromoValidation();
      form.setError("promoCode", {
        message: "Unable to validate code right now. Please try again."
      });
    }
  };
  const ensurePromoCodeValidated = async (value_0: string) => {
    const trimmed_0 = value_0.trim();
    if (!trimmed_0) {
      return true;
    }
    if (validatedPromoCode === trimmed_0 && !form.formState.errors.promoCode) {
      return true;
    }
    await handlePromoCodeBlur(trimmed_0);
    return !form.getFieldState("promoCode").error;
  };
  const country = (billingAddress?.country ?? "") as SupportedSignupCountry | "";
  const postalCode = billingAddress?.postalCode ?? "";
  const isBillingComplete = country !== "" && postalCode.trim().length > 0;
  const postalField = country ? POSTAL_FIELD_BY_COUNTRY[country] : null;
  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
    // The PaymentElement no longer collects the billing address (see the
    // `fields.billingDetails.address: "never"` option below); it only reports
    // card completeness. The country + postal code come from our own inputs.
    setIsCardComplete(event.complete);
    setStripeError(null);
  };
  const handleCountryChange = (nextCountry: string) => {
    setBillingAddress(prev => ({
      // reset the postal code so a value from a previously selected country
      // can't linger against a different country's format
      postalCode: prev?.country === nextCountry ? prev?.postalCode ?? null : "",
      country: nextCountry
    }));
    setStripeError(null);
  };
  const handlePostalCodeChange = (nextPostalCode: string) => {
    setBillingAddress(prev_0 => ({
      postalCode: nextPostalCode,
      country: prev_0?.country ?? null
    }));
    setStripeError(null);
  };
  const {
    mutate: createStripeCompanySubscription
  } = useCreateStripeCompanySubscription();
  const runSyncAndCreateSubscription = (address: {
    postalCode: string;
    country: string;
  }) => {
    const promoCode = form.getValues("promoCode");
    syncStripeCompanySetupIntent({
      setupIntentId,
      billingAddress: {
        postalCode: address.postalCode,
        country: address.country
      }
    }, {
      onSuccess: () => {
        createStripeCompanySubscription({
          tier: selectedPlan as CreatePlanSubscriptionInput["tier"],
          billingInterval: getSubscriptionBillingInterval(),
          promoCode: promoCode?.trim() || undefined
        }, {
          onSuccess: () => {
            setIsProcessing(false);
            onPaymentSuccess?.();
          },
          onError: error => {
            const graphqlErrors = (error as {
              response?: {
                errors?: Array<{
                  message?: string;
                }>;
              };
            })?.response?.errors;
            const message = graphqlErrors?.[0]?.message ?? (error instanceof Error ? error.message : null) ?? "Something went wrong. Please try again.";
            if (message?.toLowerCase().includes("promo") || message?.toLowerCase().includes("promotion")) {
              form.setError("promoCode", {
                message: PROMO_CODE_INVALID_MESSAGE
              });
            } else {
              setStripeError(message);
            }
            setIsProcessing(false);
          }
        });
      },
      onError: () => {
        setStripeError("Failed to update billing address. Please try again.");
        setIsProcessing(false);
      }
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    if (isValidatingPromo) {
      return;
    }
    if (form.formState.errors.promoCode) {
      return;
    }
    const promoCodeValue = form.getValues("promoCode") ?? "";
    const isPromoValid = await ensurePromoCodeValidated(promoCodeValue);
    if (!isPromoValid) {
      return;
    }

    // The submit button is gated on a selected country + non-empty postal code,
    // so both are guaranteed here; these stay as defensive guards.
    if (!billingAddress?.country) {
      setStripeError("Please select your country.");
      return;
    }
    if (!billingAddress.postalCode?.trim()) {
      const postalLabel = POSTAL_FIELD_BY_COUNTRY[billingAddress.country as SupportedSignupCountry]?.label ?? "postal code";
      setStripeError(`Please enter your ${postalLabel.toLowerCase()}.`);
      return;
    }
    const address_0 = {
      postalCode: billingAddress.postalCode.trim(),
      country: billingAddress.country
    };
    setIsProcessing(true);
    setStripeError(null);

    // already confirmed this SetupIntent (e.g. user corrected zip and retried)—skip confirm, just sync + create
    if (setupIntentConfirmedRef.current) {
      runSyncAndCreateSubscription(address_0);
      return;
    }
    const result_0 = await stripe.confirmSetup({
      elements,
      // The PaymentElement isn't collecting the billing address
      // (fields.billingDetails.address: "never"), so Stripe requires the full
      // address object here. A card only needs country + postal code, which we
      // collect ourselves; the remaining fields are passed as empty strings to
      // satisfy Stripe's "opted-out fields must be supplied" requirement.
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              line1: "",
              line2: "",
              city: "",
              state: "",
              postal_code: address_0.postalCode,
              country: address_0.country
            }
          }
        }
      },
      redirect: "if_required"
    });
    if (result_0.error) {
      setStripeError(result_0.error.message || "There was an error processing your payment. Please try again.");
      setIsProcessing(false);
    } else {
      setupIntentConfirmedRef.current = true;
      runSyncAndCreateSubscription(address_0);
    }
  };
  if (!planDetails) {
    return null;
  }
  return <div className="flex flex-col gap-1">
      <div className="mb-6">
        {hidePlanChange ? null : <div className="flex items-center justify-between mb-3">
            <Body className="text-sm text-foreground-muted">Selected plan</Body>
            <button onClick={() => onChangePlan?.()} className="text-sm hover:underline text-brand">
              Change
            </button>
          </div>}

        <div className="border rounded p-4 flex items-center justify-between bg-background-muted border-border">
          <span className="text-sm text-foreground">{planDetails.name}</span>
          <div className="text-right">
            {hasDiscount ? <span className="text-sm text-foreground-muted line-through mr-1.5">
                {basePriceFormatted}
              </span> : null}
            <span className="text-sm text-foreground">{priceToPay}</span>

            <span className="text-sm text-foreground-muted">
              {" "}
              / {billingInterval === "MONTHLY" ? "month" : "year"}
            </span>
          </div>
        </div>

        {isFree ? <p className="mt-3 text-xs leading-relaxed text-foreground-muted">
            Your card will be kept on file for easy upgrades. You won't be
            charged unless you upgrade your plan.
          </p> : <p className="mt-3 text-xs leading-relaxed text-foreground-muted">
            You're about to purchase a new subscription. You'll be charged{" "}
            {priceToPay} plus applicable tax immediately for Homeworks{" "}
            {planDetails.name.replace(" plan", "")}, billed{" "}
            {billingInterval === "MONTHLY" ? "monthly" : "yearly, with automatic yearly renewal"}
            . Cancel any time.
          </p>}
      </div>

      {/* Stripe Payment Element */}
      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {stripeError && <div className="p-3 rounded text-sm bg-status-error-lighten-3 border border-status-error-lighten-2 text-status-error-darken-2">
              {stripeError}
            </div>}

          <FormField control={form.control} name="promoCode" render={({
          field
        }) => <FormItem className="relative">
                <FormLabel weight="medium">
                  Referral code{" "}
                  <span className="font-normal text-foreground-muted">
                    (optional)
                  </span>
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter referral code" onChange={event_0 => {
              field.onChange(event_0);
              if (validatedPromoCode !== null) {
                resetPromoValidation();
              }
              form.clearErrors("promoCode");
            }} onBlur={() => {
              field.onBlur();
              void handlePromoCodeBlur(field.value ?? "");
            }} className={cn(form.formState.errors?.promoCode ? "border-status-error" : "", isValidatingPromo ? "pr-10" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                {isValidatingPromo ? <div className="absolute right-3 top-[2.125rem] pointer-events-none">
                    <LoadingSpinner className="size-4" />
                  </div> : null}
                {promoValidation?.isValid ? <p className="text-xs text-status-success">
                    {promoValidation.details?.description ?? "Valid code"}
                  </p> : null}
                <FormMessage />
              </FormItem>} />

          <PaymentElement onChange={handlePaymentElementChange} options={{
          layout: {
            type: "auto",
            defaultCollapsed: false
          },
          // The billing country + postal code are collected by our own
          // inputs below (so the country list can be restricted); keep the
          // address out of this element.
          fields: {
            billingDetails: {
              address: "never"
            }
          },
          // We render our own card authorization text below the country
          // input, so suppress Stripe's built-in mandate to avoid duplicates.
          terms: {
            card: "never"
          }
        }} className="p-[3px]" />

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="billing-country">Country</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Where Homeworks is available" className="text-foreground-muted hover:text-foreground">
                    <Info size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm leading-normal text-popover-foreground">
                    {SUPPORTED_COUNTRIES_NOTICE}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Select value={country} onValueChange={handleCountryChange}>
              <SelectTrigger id="billing-country" className={AUTH_FLOW_INPUT_CLASSNAME} size="lg">
                <SelectValue placeholder="Select your country" />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_SIGNUP_COUNTRY_OPTIONS.map(option => <SelectItem key={option.value} value={option.value}>
                    <span className="mr-2" aria-hidden>
                      {option.flag}
                    </span>
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {postalField && <div className="flex flex-col gap-2">
              <Label htmlFor="billing-postal-code">{postalField.label}</Label>
              <Input id="billing-postal-code" type="text" autoComplete="postal-code" placeholder={postalField.placeholder} value={postalCode} onChange={e_0 => handlePostalCodeChange(e_0.target.value)} className={AUTH_FLOW_INPUT_CLASSNAME} sizeVariant="lg" />
            </div>}

          <p className="mt-2 text-xs text-[#6d6e78]">
            By providing your card information, you allow Homeworks to charge
            your card for future payments in accordance with their terms.
          </p>

          <Button type="submit" disabled={!isCardComplete || !isBillingComplete || isProcessing || isValidatingPromo || !!form.formState.errors.promoCode} className="w-full text-surface bg-brand hover:bg-brand-hover active:bg-brand-hover h-12 mt-6">
            {isProcessing ? <LoadingSpinner /> : isFree ? "Continue" : `Pay ${priceToPay}`}
          </Button>
        </form>
      </Form>
    </div>;
}
export function Purchase({
  selectedPlan,
  billingInterval,
  wrapInCard = true,
  onChangePlan,
  onPaymentSuccess
}: PurchaseProps) {
  const setupIntentInitialized = useRef(false);
  const [setupIntentData, setSetupIntentData] = useState<{
    clientSecret: string;
    setupIntentId: string;
  } | null>(null);
  const {
    mutateAsync: createSetupIntent,
    isError,
    error
  } = useStripeSetupIntent();
  useEffect(() => {
    if (!setupIntentInitialized.current) {
      createSetupIntent().then(data => {
        setSetupIntentData(data);
      }).catch(err => {
        console.error("Failed to create setup intent:", err);
      });
      setupIntentInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clientSecret = setupIntentData?.clientSecret;
  const Wrapper = wrapInCard ? AuthFlowCard : React.Fragment;
  if (!clientSecret && !isError) {
    return <Wrapper title="Purchase plan">
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-brand-gray-lighter-1">
            Loading payment form...
          </div>
        </div>
      </Wrapper>;
  }
  if (isError || !clientSecret) {
    return <Wrapper title="Purchase plan">
        <div className="flex flex-col items-center justify-center py-8 gap-4">
          <div className="p-3 rounded text-sm text-status-error">
            {error instanceof Error ? error.message : "Failed to initialize payment form. Please try again."}
          </div>
          <button onClick={() => {
          setupIntentInitialized.current = false;
          setSetupIntentData(null);
          createSetupIntent().then(data => {
            setSetupIntentData(data);
          }).catch(err => {
            console.error("Failed to create setup intent:", err);
          });
        }} className="px-4 py-2 text-sm rounded text-surface bg-brand">
            Retry
          </button>
        </div>
      </Wrapper>;
  }
  return <Elements options={{
    clientSecret,
    appearance: {
      variables: {
        colorPrimary: "var(--color-primary)",
        // larger spacing unit makes inputs and layout more spacious (height is not in Stripe's allowlist for .Input)
        spacingUnit: "8px",
        fontSizeBase: "16px"
      },
      rules: {
        ".Input": {
          paddingTop: "14px",
          paddingBottom: "14px",
          paddingLeft: "8px"
        },
        ".Label": {
          fontWeight: "500",
          fontSize: "14px",
          lineHeight: "14px"
        },
        ".TermsText": {
          fontSize: "12px"
        }
      }
    }
  }} stripe={stripePromise}>
      <Wrapper title="Purchase plan">
        <PurchaseForm selectedPlan={selectedPlan} billingInterval={billingInterval} onChangePlan={onChangePlan} onPaymentSuccess={onPaymentSuccess} setupIntentId={setupIntentData?.setupIntentId} hidePlanChange={!wrapInCard} />
      </Wrapper>
    </Elements>;
}